import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Message, MessageType } from 'types/chat.types';
import { UserFileMessage, SystemMessage, ThinkingIndicator } from 'components/molecules/chat';
import ProcessingMessage from 'components/organisms/chat/ProcessingMessage';
import ResultMessage from 'components/organisms/chat/ResultMessage';
import ErrorMessageDisplay from 'components/organisms/chat/ErrorMessageDisplay';
import VerificationMessage from 'components/organisms/chat/VerificationMessage';
import ReasoningTimeline from 'components/organisms/chat/ReasoningTimeline';
import AgentQuestionMessage from 'components/organisms/chat/AgentQuestionMessage';
import HTSClassificationResult from 'components/organisms/chat/HTSClassificationResult';
import JsonViewer from 'components/molecules/JsonViewer';
import Icon from 'components/atoms/Icon';
import { parseClassificationContent } from 'utils/classificationParser';
import classfiLogo from 'assets/logo/classfi_no_name.png';
import useAuthStore from 'stores/authStore';

/**
 * Detect if a string is a JSON object/array and return parsed value if so.
 */
const tryParseJson = (text: string): unknown | null => {
  const trimmed = text.trim();
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Renders content as either a JsonViewer (if it's valid JSON) or ReactMarkdown.
 */
const SmartContent: React.FC<{ content: string; textColor?: string }> = ({ content, textColor = '#262626' }) => {
  const parsed = tryParseJson(content);
  if (parsed !== null) {
    return <JsonViewer data={parsed} defaultExpanded />;
  }
  return (
    <ReactMarkdown
      components={{
        p: ({node, ...props}) => <p className={`mb-2 last:mb-0`} style={{ color: textColor }} {...props} />,
        strong: ({node, ...props}) => <strong className="font-bold" style={{ color: textColor }} {...props} />,
        em: ({node, ...props}) => <em className="italic" style={{ color: textColor }} {...props} />,
        code: ({node, ...props}) => <code className="bg-gray-100 px-1 py-0.5 rounded text-[12px]" style={{ color: textColor }} {...props} />,
        ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2" style={{ color: textColor }} {...props} />,
        ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2" style={{ color: textColor }} {...props} />,
        li: ({node, ...props}) => <li className="mb-1" style={{ color: textColor }} {...props} />,
        h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2" style={{ color: textColor }} {...props} />,
        h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2" style={{ color: textColor }} {...props} />,
        h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-2" style={{ color: textColor }} {...props} />,
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

interface MessageBubbleProps {
  message: Message;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message }) => {
  const user = useAuthStore((state) => state.user);
  const userInitials = user
    ? `${user.givenName?.[0] || ''}${user.familyName?.[0] || ''}`.toUpperCase() || user.name?.[0]?.toUpperCase() || 'U'
    : 'U';
  const displayName = user
    ? [user.givenName, user.familyName].filter(Boolean).join(' ') || user.name || 'User'
    : 'User';

  const renderMessageContent = () => {
    switch (message.type) {
      case MessageType.USER_FILE:
        return <UserFileMessage message={message} />;

      case MessageType.USER_TEXT:
        return (
          <div className="self-end flex flex-col gap-[5px] items-end">
            <div className="flex gap-2 items-center">
              <div className="w-[25px] h-[25px] rounded-full bg-[#d4a574] flex items-center justify-center text-white text-xs font-bold">
                {userInitials}
              </div>
              <div className="flex gap-2 items-center">
                <p className="font-bold text-[#262626] text-[13px] leading-[1.2]">{displayName}</p>
                <span className="text-[#8c8c8c] text-[13px] font-medium leading-[1.2]">
                  | {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            <div className="bg-[#effbf2] border border-[rgba(14,116,102,0.1)] text-[#263238] px-4 py-2 rounded-[16px] max-w-2xl">
              <div className="text-[13px] leading-[1.7] font-medium prose prose-sm max-w-none">
                {/* eslint-disable jsx-a11y/heading-has-content */}
                <ReactMarkdown
                  components={{
                    p: ({node, ...props}) => <p className="mb-2 last:mb-0 text-[#263238]" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-[#263238]" {...props} />,
                    em: ({node, ...props}) => <em className="italic text-[#263238]" {...props} />,
                    code: ({node, ...props}) => <code className="bg-gray-200 px-1 py-0.5 rounded text-[#263238]" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2 text-[#263238]" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2 text-[#263238]" {...props} />,
                    li: ({node, ...props}) => <li className="mb-1 text-[#263238]" {...props} />,
                    h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2 text-[#263238]" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2 text-[#263238]" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-2 text-[#263238]" {...props} />,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
                {/* eslint-enable jsx-a11y/heading-has-content */}
              </div>
            </div>
          </div>
        );

      case MessageType.ASSISTANT:
        return (
          <div className="self-start">
            <div
              className="bg-white border border-[rgba(224,224,224,0.75)] p-4 rounded-[20px] max-w-2xl flex flex-col gap-2"
              style={{ boxShadow: '0px 40px 48px -20px rgba(0,0,0,0.02)' }}
            >
              {/* Header */}
              <div className="flex items-center gap-2">
                <div className="w-[34px] h-[34px] flex items-center justify-center">
                  <img src={classfiLogo} alt="Classfi" className="w-full h-full object-contain" />
                </div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-[#263238] text-[14px] leading-[1.4] tracking-[-0.084px]">Classfi</p>
                  <span className="text-[#8c8c8c] text-[13px] font-medium leading-[1.2]">
                    | {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="text-[14px] leading-[1.7] font-medium prose prose-sm max-w-none text-[#262626]">
                <SmartContent content={message.content || ''} textColor="#262626" />
              </div>
            </div>
          </div>
        );

      case MessageType.THINKING:
        return <ThinkingIndicator />;

      case MessageType.PROCESSING:
        return <ProcessingMessage message={message} />;

      case MessageType.RESULT:
        return <ResultMessage message={message} />;

      case MessageType.CLASSIFICATION_RESULT:
        const classificationMsg = message as import('types/chat.types').ClassificationResultMessage;
        const structuredResult = classificationMsg.result;

        // New format: reloaded conversations carry the same structured result as
        // the live stream, so render through the exact same path (ResultMessage)
        // to guarantee the chat output and the library view stay identical —
        // including the embedded, brand-aligned tariff determination section.
        if (structuredResult?.hts_classification) {
          return (
            <ResultMessage
              message={{
                id: classificationMsg.id,
                type: MessageType.RESULT,
                timestamp: classificationMsg.timestamp,
                result: structuredResult,
              }}
            />
          );
        }

        // Legacy format: parse from markdown content string
        const parsedData = parseClassificationContent(message.content || '');
        if (!parsedData) {
          return (
            <div className="self-start">
              <div
                className="bg-white border border-[rgba(224,224,224,0.75)] p-4 rounded-[20px] max-w-2xl flex flex-col gap-2"
                style={{ boxShadow: '0px 40px 48px -20px rgba(0,0,0,0.02)' }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-[34px] h-[34px] flex items-center justify-center">
                    <img src={classfiLogo} alt="Classfi" className="w-full h-full object-contain" />
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-[#263238] text-[14px] leading-[1.4] tracking-[-0.084px]">Classfi</p>
                    <span className="text-[#8c8c8c] text-[13px] font-medium leading-[1.2]">
                      | {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
                <div className="text-[14px] leading-[1.7] font-medium prose prose-sm max-w-none text-[#262626]">
                  <SmartContent content={message.content || ''} textColor="#262626" />
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="self-start w-full flex flex-col">
            <div className="bg-gray-50 px-4 py-4 rounded-2xl rounded-bl-md max-w-3xl">
              <div className="flex items-center gap-2 mb-4 text-xs text-gray-500">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <Icon name="check" className="w-2.5 h-2.5 text-white" />
                </div>
                <span>Classification Complete</span>
              </div>

              <HTSClassificationResult
                data={parsedData.data}
                filename={parsedData.filename}
                classificationId={parsedData.classificationId}
                documentId={parsedData.classificationId}
              />
            </div>
            <div className="text-xs text-gray-400 mt-1 ml-2">
              {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        );

      case MessageType.ERROR:
        return <ErrorMessageDisplay message={message} />;

      case MessageType.SYSTEM:
        return <SystemMessage message={message} />;

      // Reasoning chain from iterative HTS agent
      case MessageType.REASONING:
        return <ReasoningTimeline message={message} />;

      // Agent asking user for clarification mid-classification
      case MessageType.AGENT_QUESTION:
        return <AgentQuestionMessage message={message} />;

      // NEW: Verification message type
      case MessageType.VERIFICATION_QUESTION:
        return <VerificationMessage message={message} />;

      // NEW: User's verification response (simple text display)
      case MessageType.VERIFICATION_RESPONSE:
        return (
          <div className="self-end flex flex-col gap-[5px] items-end">
            <div className="flex gap-2 items-center">
              <div className="w-[25px] h-[25px] rounded-full bg-[#d4a574] flex items-center justify-center text-white text-xs font-bold">
                {userInitials}
              </div>
              <div className="flex gap-2 items-center">
                <p className="font-bold text-[#262626] text-[13px] leading-[1.2]">{displayName}</p>
                <span className="text-[#8c8c8c] text-[13px] font-medium leading-[1.2]">
                  | {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            <div className="bg-[#effbf2] border border-[rgba(14,116,102,0.1)] text-[#263238] px-4 py-2 rounded-[16px] max-w-2xl">
              <div className="text-[13px] leading-[1.7] font-medium prose prose-sm max-w-none">
                {/* eslint-disable jsx-a11y/heading-has-content */}
                <ReactMarkdown
                  components={{
                    p: ({node, ...props}) => <p className="mb-2 last:mb-0 text-[#263238]" {...props} />,
                    strong: ({node, ...props}) => <strong className="font-bold text-[#263238]" {...props} />,
                    em: ({node, ...props}) => <em className="italic text-[#263238]" {...props} />,
                    code: ({node, ...props}) => <code className="bg-gray-200 px-1 py-0.5 rounded text-[#263238]" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-2 text-[#263238]" {...props} />,
                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-2 text-[#263238]" {...props} />,
                    li: ({node, ...props}) => <li className="mb-1 text-[#263238]" {...props} />,
                    h1: ({node, ...props}) => <h1 className="text-lg font-bold mb-2 text-[#263238]" {...props} />,
                    h2: ({node, ...props}) => <h2 className="text-base font-bold mb-2 text-[#263238]" {...props} />,
                    h3: ({node, ...props}) => <h3 className="text-sm font-bold mb-2 text-[#263238]" {...props} />,
                  }}
                >
                  {message.userResponse}
                </ReactMarkdown>
                {/* eslint-enable jsx-a11y/heading-has-content */}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const content = renderMessageContent();

  if (!content) {
    return null;
  }

  return (
    <div className="flex flex-col max-w-3xl break-words w-full">
      {content}
    </div>
  );
};

export default MessageBubble;
